import { StageMatchTab } from 'modules/championship/domain/enums/stage-match-tab';
import type { LatestMatchDto } from 'modules/main/domain/graphql/latest-matches';
import type { LatestMatch, LatestMatchTeam } from 'modules/main/domain/interfaces/latest-match';
import { Pages } from 'modules/navigation/domain/enums/pages';

/** Бэкенд отдаёт смещение без двоеточия (`+0000`) — Safari такую дату не разбирает; приводим к `+00:00`. */
function toTimestamp(value: string | null): number | null {
  if (!value) return null;

  const time = Date.parse(value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
  return Number.isNaN(time) ? null : time;
}

/** Страница матча — только у матча на этапе; без него ведём в турнир, чтобы строка не была глухой. */
function toPath(match: LatestMatchDto): string | null {
  const stageId = match.round?.stage?.id;

  if (match.championship && stageId) {
    return Pages.CHAMPIONSHIPS.STEPS.builders.match(match.championship.id, stageId, match.id, StageMatchTab.RESULT);
  }

  return match.championship ? Pages.CHAMPIONSHIPS.builders.edit(match.championship.id) : null;
}

/**
 * Прошедшие матчи → строки ленты. Матч, в котором не набралось двух команд или некуда вести, пропускается:
 * «Команда А — ???» в ленте читается как поломка.
 */
export function mapLatestMatches(matches: LatestMatchDto[]): LatestMatch[] {
  const result: LatestMatch[] = [];

  matches.forEach((match) => {
    const teams = [...(match.teamMatches ?? [])]
      .filter((teamMatch) => teamMatch.team)
      .sort((a, b) => (a.teamOrder ?? 0) - (b.teamOrder ?? 0))
      .map<LatestMatchTeam>((teamMatch) => ({
        name: teamMatch.team?.name ?? '',
        emblemUrl: teamMatch.team?.emblem?.externalUrl ?? null,
        score: teamMatch.score,
      }));

    const path = toPath(match);
    if (teams.length < 2 || !path) return;

    const [first, second] = teams;
    const score = first.score !== null && second.score !== null ? `${first.score}:${second.score}` : '—';
    const title = `${first.name} ${score} ${second.name}`;

    result.push({
      id: match.id,
      title,
      description: match.championship ? `${match.championship.name}. ${title}` : title,
      playedAt: toTimestamp(match.date),
      teams: [first, second],
      path,
    });
  });

  return result;
}
